import type {
  DefaultError,
  MutationFunctionContext,
  MutationKey,
  MutationMeta,
  MutationScope,
} from "@tanstack/vue-query";
import type { AxiosRequestConfig, AxiosResponse } from "axios";
import type { ResourceKeyFactory } from "../Query/resourceKeys";
import { mutationOptions } from "@tanstack/vue-query";
import { getQueryClient } from "@verbleif/lib/src/composables/Query/queryClient";
import { api } from "../Api/api";
import { invalidateResources } from "../Query/invalidateResource";
import { itemKey } from "../Query/resourceKeys";

export interface MutationUrlVariables<TBody = unknown> {
  url: string
  body?: TBody
  config?: AxiosRequestConfig
}

export interface MutationDeleteVariables {
  url: string
  config?: AxiosRequestConfig
}

type InvalidatesOption<TData, TVariables>
  = | ResourceKeyFactory[]
    | ((data: TData, variables: TVariables) => ResourceKeyFactory[]);

interface PassThroughOptions<TData, TError, TVariables, TOnMutateResult> {
  mutationKey?: MutationKey
  onMutate?: (
    variables: TVariables,
    context: MutationFunctionContext,
  ) => Promise<TOnMutateResult> | TOnMutateResult
  onSuccess?: (
    data: TData,
    variables: TVariables,
    onMutateResult: TOnMutateResult,
    context: MutationFunctionContext,
  ) => Promise<unknown> | unknown
  onError?: (
    error: TError,
    variables: TVariables,
    onMutateResult: TOnMutateResult | undefined,
    context: MutationFunctionContext,
  ) => Promise<unknown> | unknown
  onSettled?: (
    data: TData | undefined,
    error: TError | null,
    variables: TVariables,
    onMutateResult: TOnMutateResult | undefined,
    context: MutationFunctionContext,
  ) => Promise<unknown> | unknown
  scope?: MutationScope
  meta?: MutationMeta
  retry?: boolean | number | ((failureCount: number, error: TError) => boolean)
  gcTime?: number
  networkMode?: "online" | "always" | "offlineFirst"
  throwOnError?: boolean | ((error: TError) => boolean)
}

export type HydraMutationOptions<TData, TError, TVariables, TOnMutateResult = unknown>
  = PassThroughOptions<TData, TError, TVariables, TOnMutateResult> & {
    invalidates?: InvalidatesOption<TData, TVariables>
    awaitInvalidation?: boolean
  };

type ItemKeyEffect = "invalidate" | "remove" | "none";

async function applyItemKeyEffect(effect: ItemKeyEffect, url: string): Promise<void> {
  const client = getQueryClient();
  if (effect === "invalidate") {
    await client.invalidateQueries({ queryKey: itemKey(url), refetchType: "none" });
    return;
  }
  if (effect === "remove") {
    client.removeQueries({ queryKey: itemKey(url) });
  }
}

async function invalidateFactories<TData, TVariables>(
  invalidates: InvalidatesOption<TData, TVariables> | undefined,
  data: TData,
  variables: TVariables,
): Promise<void> {
  if (!invalidates) {
    return;
  }
  await invalidateResources(typeof invalidates === "function" ? invalidates(data, variables) : invalidates);
}

function hydraMutation<TData, TError, TVariables extends { url: string }, TOnMutateResult>(
  request: (variables: TVariables) => Promise<AxiosResponse<TData>>,
  itemKeyEffect: ItemKeyEffect,
  options: HydraMutationOptions<TData, TError, TVariables, TOnMutateResult>,
) {
  const { invalidates, onSuccess, awaitInvalidation = true, ...rest } = options;
  return mutationOptions({
    ...rest,
    mutationFn: async (variables: TVariables) => {
      const response = await request(variables);
      return response.data;
    },
    onSuccess: async (data: TData, variables: TVariables, onMutateResult: TOnMutateResult, context: MutationFunctionContext) => {
      if (awaitInvalidation) {
        await invalidateFactories(invalidates, data, variables);
        await applyItemKeyEffect(itemKeyEffect, variables.url);
        await onSuccess?.(data, variables, onMutateResult, context);
        return;
      }
      void invalidateFactories(invalidates, data, variables).catch(() => undefined);
      void applyItemKeyEffect(itemKeyEffect, variables.url).catch(() => undefined);
      await onSuccess?.(data, variables, onMutateResult, context);
    },
  });
}

export function postMutation<
  TData,
  TBody = unknown,
  TError = DefaultError,
  TOnMutateResult = unknown,
>(
  options: HydraMutationOptions<TData, TError, MutationUrlVariables<TBody>, TOnMutateResult> = {},
) {
  return hydraMutation(
    (variables: MutationUrlVariables<TBody>) => api.post<TData>(variables.url, variables.body, variables.config),
    "none",
    options,
  );
}

export function patchMutation<
  TData,
  TBody = unknown,
  TError = DefaultError,
  TOnMutateResult = unknown,
>(
  options: HydraMutationOptions<TData, TError, MutationUrlVariables<TBody>, TOnMutateResult> = {},
) {
  return hydraMutation(
    (variables: MutationUrlVariables<TBody>) => api.patch<TData>(variables.url, variables.body, variables.config),
    "invalidate",
    options,
  );
}

export function putMutation<
  TData,
  TBody = unknown,
  TError = DefaultError,
  TOnMutateResult = unknown,
>(
  options: HydraMutationOptions<TData, TError, MutationUrlVariables<TBody>, TOnMutateResult> = {},
) {
  return hydraMutation(
    (variables: MutationUrlVariables<TBody>) => api.put<TData>(variables.url, variables.body, variables.config),
    "invalidate",
    options,
  );
}

export function deleteMutation<
  TData = void,
  TError = DefaultError,
  TOnMutateResult = unknown,
>(
  options: HydraMutationOptions<TData, TError, MutationDeleteVariables, TOnMutateResult> = {},
) {
  return hydraMutation(
    (variables: MutationDeleteVariables) => api.delete<TData>(variables.url, variables.config),
    "remove",
    options,
  );
}
